import { SubmitButton } from "@/components/Global/Button/Button";
import { FormErrorState, Input } from "@/components/Global/Input/Input";
import { Section } from "@/components/Global/Section/Section";
import { useAppContext } from "@/context/AppContext";
import { UserType } from "@/types/baseTypes";
import { authenticateUser, fetchUser } from "@/utils/serviceAPI/user";
import { LoginSchema, safeParseSchema } from "@/utils/validations";
import { useGlobalContext } from "@aleksundshantu.com/aunds-utils-react/contexts";
import router from "next/router";
import React, { FormEvent, useEffect, useState } from "react";
import { LoginNavigation } from "../SharedComponents/LoginNavigation/LoginNavigation";
import styles from "./Login.module.scss";
import { ReactSVG } from "react-svg";
import { customSanitize } from "@/utils/customSanitize";
import { ExternButton } from "../SharedComponents/ExternButton/ExternButton";
import { externalLogin } from "../utils/utils";
import { useModal } from "@/components/_primitives/Modal/ModalRoot/ModalRoot";
import { Loader } from "@/components/Loader/Loader";

function Login() {
  const { getLabel, getIcon } = useGlobalContext();
  const { setUser, setFormModalText, setFormModalType, user } = useAppContext();
  const [loading, setLoading] = React.useState(false);
  // redirect without delay to /profile if user is already logged in
  useEffect(() => {
    if (user) {
      router.push("/profile");
      setLoading(true);
    }
  }, [user]);
  const { setOpen } = useModal();

  const validationMessages = {
    email_invalid: getLabel("error_email_invalid"),
    required_field: getLabel("error_required_field"),
  };
  const [errors, setErrors] = useState<FormErrorState>();

  /* jscpd:ignore-start */
  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const formData = new FormData(e.currentTarget);
    const formProps = Object.fromEntries(formData);
    const { data, flattenedErrors } = safeParseSchema(
      LoginSchema(validationMessages),
      formProps,
    );

    if (data) {
      setLoading(true);
      setErrors({});
      try {
        await authenticateUser(data as UserType, router.locale);
        sessionStorage.setItem("loggedIn", "true");
        const userRes = await fetchUser(undefined, router.locale);
        if (!userRes || userRes.data == null)
          throw new Error(
            "No user data could be fetched, potentially due to cookie issues",
          );
        setUser(userRes?.data);
        sessionStorage.setItem("userData", JSON.stringify(userRes.data));
      } catch (err) {
        setLoading(false);
        setOpen(true);
        setFormModalText(
          "Der Login ist fehlgeschlagen. Email oder Passwort ist falsch.",
        );
        console.error("Login failed:", err);
      }
    } else {
      setErrors(flattenedErrors);
    }
  };
  /* jscpd:ignore-end */
  const openResetPasswordModal = () => {
    setOpen(true);
    setFormModalType("reset");
  };

  return (
    <Section columnsClassName="is-centered is-multiline">
      <div className="column is-10-tablet is-8-desktop pb-0">
        <LoginNavigation
          label={getLabel("login_navigation_label_registration")}
          href="/registrierung"
        />
      </div>
      <div className="column is-10-tablet is-8-desktop is-6-widescreen">
        <form onSubmit={handleSubmit} autoComplete="on">
          <Input
            inputType="email"
            name="email"
            label={getLabel("login_form_label_email")}
            placeholder={getLabel("login_form_placeholder_email")}
            errors={errors}
          ></Input>
          <Input
            inputType="password"
            name="password"
            label={getLabel("login_form_label_password")}
            placeholder={getLabel("login_form_placeholder_password")}
            errors={errors}
            aria-autocomplete="list"
          ></Input>
          <button
            type="button"
            className={`button-reset btn ${styles["resetpw-btn"]}`}
            onClick={() => openResetPasswordModal()}
          >
            {getLabel("login_form_label_button_password_forgot")}
            <ReactSVG
              wrapper="svg"
              src={getIcon("arrowRight")}
              className={styles["arrow-icon"]}
              width={12}
              height={12}
              beforeInjection={(svg) => {
                svg.setAttribute("alt", "Arrow right Icon");
                svg.setAttribute("aria-label", "Arrow right Icon");
              }}
            />
          </button>

          <>
            <SubmitButton className="login-submit-buton">
              {getLabel("login_form_label_button_intern")}
            </SubmitButton>
            <ExternButton
              onClick={(clickEvent: React.MouseEvent<HTMLButtonElement>) => {
                clickEvent.preventDefault();
                externalLogin("google");
              }}
              label={getLabel("login_form_label_button_google")}
            />
          </>

          {loading && <Loader />}
        </form>
        <p
          className="has-text-centered mt-6"
          dangerouslySetInnerHTML={{
            __html: customSanitize(
              getLabel("login_navigation_button_registration_bottom"),
            ),
          }}
        ></p>
      </div>
    </Section>
  );
}

export default Login;
